/* eslint-disable import/no-extraneous-dependencies */
import React from 'react';
import ReactDOM from 'react-dom';
import Model, { Providers } from '@tripian/model';
import classes from './KiwiSpecialOffersModal.scss';

interface IKiwiSpecialOffersModal {
  hotelName: string;
  specialOffers?: Providers.Kiwi.KiwiSpecialOffer[];
  onClose: () => void;
  t: (value: Model.TranslationKey) => string;
}

const stripHtml = (html: string): string => html.replace(/<[^>]*>/g, '').trim();

const KiwiSpecialOffersModal: React.FC<IKiwiSpecialOffersModal> = ({ hotelName, specialOffers, onClose, t }) =>
  ReactDOM.createPortal(
    <div className={classes.overlay} onClick={(e) => { e.stopPropagation(); onClose(); }} role="presentation">
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div className={classes.modal} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className={classes.header}>
          <div className={classes.headerLeft}>
            <svg className={classes.tagIcon} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" fill="currentColor">
              <path d="M21.41 11.58l-9-9C12.05 2.22 11.55 2 11 2H4c-1.1 0-2 .9-2 2v7c0 .55.22 1.05.59 1.42l9 9c.36.36.86.58 1.41.58s1.05-.22 1.41-.59l7-7c.37-.36.59-.86.59-1.41s-.23-1.06-.59-1.42zM5.5 7C4.67 7 4 6.33 4 5.5S4.67 4 5.5 4 7 4.67 7 5.5 6.33 7 5.5 7z" />
            </svg>
            <span className={classes.title}>{t('kiwi.specialOffers.title')}</span>
          </div>
          <button type="button" className={classes.closeBtn} onClick={onClose}>
            <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" fill="currentColor">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
            </svg>
          </button>
        </div>

        <div className={classes.divider} />

        {/* Content */}
        <div className={classes.body}>
          {!specialOffers ? (
            <div className={classes.loading}>{t('kiwi.common.loading')}</div>
          ) : specialOffers.length > 0 ? (
            specialOffers.map((offer) => (
              <div key={offer.title} className={classes.offerCard}>
                <p className={classes.offerTitle}>{offer.title}</p>

                <ul className={classes.benefitsList}>
                  {offer.shortDescription && (
                    <li className={classes.benefitItem}>
                      <svg className={classes.checkIcon} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="12" cy="12" r="12" fill="#1a1f71" />
                        <path d="M9.5 15.5l-3-3 1.41-1.41L9.5 12.67l5.59-5.59L16.5 8.5z" fill="#fff" />
                      </svg>
                      <span>{stripHtml(offer.shortDescription)}</span>
                    </li>
                  )}
                  {offer.minimumNightStay && (
                    <li className={classes.benefitItem}>
                      <svg className={classes.checkIcon} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="12" cy="12" r="12" fill="#1a1f71" />
                        <path d="M9.5 15.5l-3-3 1.41-1.41L9.5 12.67l5.59-5.59L16.5 8.5z" fill="#fff" />
                      </svg>
                      <span>{offer.minimumNightStay} {t('kiwi.specialOffers.minimumNightStay')}</span>
                    </li>
                  )}
                  {offer.offerEndDate && (
                    <li className={classes.benefitItem}>
                      <svg className={classes.checkIcon} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="12" cy="12" r="12" fill="#1a1f71" />
                        <path d="M9.5 15.5l-3-3 1.41-1.41L9.5 12.67l5.59-5.59L16.5 8.5z" fill="#fff" />
                      </svg>
                      <span>{t('kiwi.specialOffers.through')} {new Date(offer.offerEndDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                    </li>
                  )}
                </ul>

                {offer.url && (
                  <a className={classes.viewOfferLink} href={offer.url} target="_blank" rel="noopener noreferrer">{t('kiwi.specialOffers.viewOffer')}</a>
                )}
              </div>
            ))
          ) : (
            <div className={classes.offerCard}>
              <p className={classes.offerTitle}>{t('kiwi.specialOffers.available')} {hotelName}</p>
              <ul className={classes.benefitsList}>
                <li className={classes.benefitItem}>
                  <svg className={classes.checkIcon} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="12" cy="12" r="12" fill="#1a1f71" />
                    <path d="M9.5 15.5l-3-3 1.41-1.41L9.5 12.67l5.59-5.59L16.5 8.5z" fill="#fff" />
                  </svg>
                  <span>{t('kiwi.specialOffers.exclusiveBenefits')}</span>
                </li>
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );

export default KiwiSpecialOffersModal;